import { useLocation, NavLink, Outlet } from 'react-router-dom'

import { useAuth } from '../context/AuthContext'
import { AdminTertiaryNav } from './AdminTertiaryNav'
import { ErrorBoundary } from './ErrorBoundary'
import { Sidebar } from './Sidebar'

const MLB_TABS: ReadonlyArray<{ to: string; label: string; end?: boolean }> = [
  { to: '/', label: 'Standings', end: true },
  { to: '/matchups', label: 'Matchups' },
  { to: '/ratings', label: 'Ratings' },
  { to: '/admin', label: 'Admin' },
  { to: '/datasets', label: 'Datasets' },
]

const PGA_TABS: ReadonlyArray<{ to: string; label: string; end?: boolean }> = [
  { to: '/pga/bets', label: 'Golf bets' },
]

export function AppLayout() {
  const { user, signOut } = useAuth()
  const location = useLocation()
  const isPga = location.pathname.startsWith('/pga')
  const isAdmin = location.pathname.startsWith('/admin')
  const tabs = isPga ? PGA_TABS : MLB_TABS

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar activeSport={isPga ? 'pga' : 'mlb'} />

      {/* Main column — offset by the collapsed sidebar width (w-14 = 56px). */}
      <div className="flex h-screen min-h-0 flex-col pl-14">
        {/* App chrome: does not scroll — page content scrolls in the outlet below. */}
        <header className="app-chrome-secondary z-40 shrink-0 border-b border-slate-200 bg-white shadow-sm">
          <div className="flex items-center justify-between px-8 py-4">
            <div className="flex items-baseline gap-2">
              {isPga ? (
                <>
                  <span className="rounded bg-emerald-600 px-1.5 py-0.5 text-[11px] font-bold tracking-wide text-white">
                    PGA
                  </span>
                  <h1 className="text-lg font-semibold text-slate-900">Sim Monster · PGA</h1>
                  <span className="text-xs text-slate-400">Mike&apos;s sheet</span>
                </>
              ) : (
                <>
                  <span className="rounded bg-blue-600 px-1.5 py-0.5 text-[11px] font-bold tracking-wide text-white">
                    MLB
                  </span>
                  <h1 className="text-lg font-semibold text-slate-900">Sim Monster · MLB</h1>
                  <span className="text-xs text-slate-400">2026 season</span>
                </>
              )}
            </div>
            <div className="flex items-center gap-3 text-sm">
              <span className="text-slate-500">{user?.email ?? user?.username}</span>
              <button
                type="button"
                onClick={() => {
                  void signOut()
                }}
                className="btn-secondary"
              >
                Sign out
              </button>
            </div>
          </div>

          <nav className="flex gap-1 px-8">
            {tabs.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end ?? false}
                className={({ isActive }) =>
                  [
                    'border-b-2 px-3 py-3 text-sm font-medium transition',
                    isActive
                      ? 'border-slate-900 text-slate-900'
                      : 'border-transparent text-slate-500 hover:border-slate-200 hover:text-slate-900',
                  ].join(' ')
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        </header>

        <main className="flex min-h-0 flex-1 flex-col overflow-hidden bg-slate-50">
          {isAdmin && (
            <div className="app-chrome-tertiary z-30 shrink-0 border-b border-slate-200 bg-slate-50 px-8 py-3 shadow-sm">
              <AdminTertiaryNav sport={isPga ? 'pga' : 'mlb'} />
            </div>
          )}

          {/* Single page scrollport — content moves under fixed secondary / tertiary chrome. */}
          <div className="app-main-scroll min-h-0 flex-1 overflow-y-auto overscroll-y-contain">
            <div className="flex min-h-full min-w-0 flex-col px-8 pb-8">
              <ErrorBoundary key={location.pathname}>
                <div className="flex min-h-0 min-w-0 flex-1 flex-col">
                  <Outlet />
                </div>
              </ErrorBoundary>
            </div>
          </div>
        </main>

        <footer className="z-40 shrink-0 border-t border-slate-200 bg-white">
          <div className="px-8 py-4 text-xs text-slate-400">
            sim-monster · data refreshed daily · built for elling + mike
          </div>
        </footer>
      </div>
    </div>
  )
}
